import { LineListModel } from './line-list-model.js'
import { LineListView } from './line-list-view.js'

export class LineListElement extends HTMLElement {
	constructor() {
		super()
		this.attachShadow({ mode: 'open' })

		this.model = new LineListModel()
		this.view = new LineListView(this.shadowRoot, this.model.palette)

		this.drag = {
			active: false,
			from: null,
			ghost: null,
			itemEl: null,
			marker: null,
			startX: 0,
			startY: 0,
			moved: false,
			originIndex: -1,
		}

		this.SCROLL_MARGIN = 100
		this.SCROLL_SPEED = 15
	}

	connectedCallback() {
		// this.view.checkBtn.addEventListener('click', () => this.verify())
		this.view.resetBtn.addEventListener('click', () => this.setup())
		document.addEventListener('ligne-selected', () => this.update())
		this.update()
	}

	update() {
		const ok = this.model.updateFromStore()
		if (!ok) return
		this.setup()
	}

	setup() {
		this.view.clear()
		const items = this.view.renderItems(this.model.correctOrder)
		this.drag.marker = this.view.drag.marker
		this.enableDrag(items)
	}

	enableDrag(items) {
		items.forEach((item) => {
			item.addEventListener('pointerdown', (e) => this.startDrag(e, item))
		})
	}

	startDrag(e, item) {
		e.preventDefault()
		this.drag.active = true
		this.drag.itemEl = item
		this.drag.from = item.parentElement === this.view.leftList ? 'left' : 'pool'
		this.drag.startX = e.clientX
		this.drag.startY = e.clientY
		this.drag.moved = false

		// ✅ mémorise la position d’origine (gauche ou droite)
		if (this.drag.from === 'left') {
			const children = Array.from(this.view.leftList.querySelectorAll('.item'))
			this.drag.originIndex = children.indexOf(item)
		} else {
			const children = Array.from(this.view.pool.querySelectorAll('.item'))
			this.drag.originIndex = children.indexOf(item)
		}

		item.classList.add('dragging')
		item.setAttribute('aria-grabbed', 'true')

		this.view.leftList.classList.add('dropTarget')

		// ghost visuel global
		const r = item.getBoundingClientRect()
		const ghost = item.cloneNode(true)
		ghost.classList.add('ghostCard')
		Object.assign(ghost.style, {
			width: r.width + 'px',
			left: e.clientX + 'px',
			top: e.clientY + 'px',
			position: 'fixed',
			pointerEvents: 'none',
			zIndex: 9999,
		})
		document.body.append(ghost)
		this.drag.ghost = ghost
		item.style.opacity = '0'

		window.addEventListener('pointermove', this.onDragMove)
		window.addEventListener('pointerup', this.endDrag)
		window.addEventListener('pointercancel', this.endDrag)
	}

	onDragMove = (e) => {
		if (!this.drag.active) return

		const dx = Math.abs(e.clientX - this.drag.startX)
		const dy = Math.abs(e.clientY - this.drag.startY)
		if (!this.drag.moved && (dx > 5 || dy > 5)) this.drag.moved = true
		if (!this.drag.moved) return

		// déplacer le ghost
		this.drag.ghost.style.left = e.clientX + 'px'
		this.drag.ghost.style.top = e.clientY + 'px'

		// auto-scroll navigateur
		this.autoScroll(e)

		// détection de la zone de drop dans le shadowRoot
		const target = this.shadowRoot.elementFromPoint(e.clientX, e.clientY)
		const left = this.view.leftList
		const right = this.view.pool

		if (target === left || left.contains(target)) {
			const where = this.computeInsertionIndex(e.clientY, left)
			this.updateMarker(where.index, left)
		} else if (target === right || right.contains(target)) {
			const where = this.computeInsertionIndex(e.clientY, right)
			this.updateMarker(where.index, right)
		} else {
			this.hideMarker()
		}
	}

	endDrag = (e) => {
		// 🧠 Si pas bougé → simple clic, ne rien faire
		if (!this.drag.moved) {
			this.cleanupDrag()
			return
		}

		const item = this.drag.itemEl
		if (!item) return

		const { leftList, pool } = this.view

		if (this.drag.marker.isConnected) {
			let { index: targetIndex } = this.currentMarkerIndex()
			let dropContainer =
				this.drag.marker.parentElement === leftList ? leftList : pool

			// ✅ Correction universelle (gauche et droite)
			const children = Array.from(dropContainer.querySelectorAll('.item'))
			const originIndex = this.drag.originIndex

			if (
				(this.drag.from === 'left' && dropContainer === leftList) ||
				(this.drag.from === 'pool' && dropContainer === pool)
			) {
				if (targetIndex > originIndex && originIndex > -1) {
					targetIndex -= 1
				}
			}

			targetIndex = Math.max(0, Math.min(targetIndex, children.length))

			// si rien ne change, ne pas réinsérer inutilement
			if (
				(this.drag.from === 'left' &&
					dropContainer === leftList &&
					targetIndex === originIndex) ||
				(this.drag.from === 'pool' &&
					dropContainer === pool &&
					targetIndex === originIndex)
			) {
				this.cleanupDrag()
				return
			}

			// retirer puis insérer
			const oldParent = item.parentElement
			if (oldParent) oldParent.removeChild(item)

			const afterChildren = Array.from(dropContainer.querySelectorAll('.item'))
			if (targetIndex >= afterChildren.length) dropContainer.append(item)
			else dropContainer.insertBefore(item, afterChildren[targetIndex])
		} else {
			// Aucun marker visible → replacer selon la source
			if (this.drag.from === 'left') leftList.append(item)
			else pool.append(item)
		}

		this.cleanupDrag()
		this.verify()
	}

	cleanupDrag() {
		const item = this.drag.itemEl
		if (item) {
			item.classList.remove('dragging')
			item.setAttribute('aria-grabbed', 'false')
			item.style.opacity = ''
		}
		if (this.drag.ghost) this.drag.ghost.remove()
		this.hideMarker()

		this.drag = {
			active: false,
			from: null,
			ghost: null,
			itemEl: null,
			marker: this.drag.marker,
			startX: 0,
			startY: 0,
			moved: false,
			originIndex: -1,
		}

		this.view.leftList.classList.remove('dropTarget')

		window.removeEventListener('pointermove', this.onDragMove)
		window.removeEventListener('pointerup', this.endDrag)
		window.removeEventListener('pointercancel', this.endDrag)
	}

	autoScroll(e) {
		const { clientY } = e
		const viewportHeight = window.innerHeight
		const scrollTop = window.scrollY

		let dy = 0
		if (clientY < this.SCROLL_MARGIN) {
			dy =
				-((this.SCROLL_MARGIN - clientY) / this.SCROLL_MARGIN) *
				this.SCROLL_SPEED
		} else if (clientY > viewportHeight - this.SCROLL_MARGIN) {
			dy =
				((clientY - (viewportHeight - this.SCROLL_MARGIN)) /
					this.SCROLL_MARGIN) *
				this.SCROLL_SPEED
		}
		if (dy !== 0) window.scrollTo({ top: scrollTop + dy, behavior: 'instant' })
	}

	computeInsertionIndex(pointerY, container) {
		const items = Array.from(container.querySelectorAll('.item'))
		if (items.length === 0) return { index: 0 }
		for (let i = 0; i < items.length; i++) {
			const rect = items[i].getBoundingClientRect()
			const mid = rect.top + rect.height / 2
			if (pointerY < mid) return { index: i }
		}
		return { index: items.length }
	}

	updateMarker(index, container) {
		const children = Array.from(container.querySelectorAll('.item'))
		if (!this.drag.marker.parentElement) {
			if (index >= children.length) container.append(this.drag.marker)
			else container.insertBefore(this.drag.marker, children[index])
		} else {
			const { index: curIdx } = this.currentMarkerIndex()
			if (curIdx !== index || this.drag.marker.parentElement !== container) {
				if (index >= children.length) container.append(this.drag.marker)
				else container.insertBefore(this.drag.marker, children[index])
			}
		}
	}

	currentMarkerIndex() {
		const parent = this.drag.marker.parentElement
		if (!parent) return { index: 0, rawIndex: -1 }
		const nodes = Array.from(parent.childNodes).filter(
			(n) => n.classList?.contains?.('item') || n === this.drag.marker
		)
		const idx = nodes.indexOf(this.drag.marker)
		let insertIndex = 0
		for (let n of nodes) {
			if (n === this.drag.marker) break
			if (n.classList?.contains?.('item')) insertIndex++
		}
		return { index: insertIndex, rawIndex: idx }
	}

	hideMarker() {
		if (this.drag.marker?.isConnected) this.drag.marker.remove()
	}

	verify() {
		const items = Array.from(this.view.leftList.querySelectorAll('.item'))
		const ids = items.map((el) => Number(el.dataset.id))
		const total = this.model.correctOrder.length
		let good = 0

		items.forEach((el, i) => {
			const isCorrect = ids[i] === i
			el.classList.remove('ok', 'bad')
			el.classList.add(isCorrect ? 'ok' : 'bad')
			if (isCorrect) good++
		})

		this.view.statusEl.textContent =
			good === total ? `Parfait ! 🎉` : `${good}/${total} corrects`
	}
}

customElements.define('tpg-line-list', LineListElement)
